import React, {useCallback, useEffect, useState} from "react";

import * as OBC from "@thatopen/components";
import { CameraTool } from "@treegram/tools-camera";

import { Slider } from "radix-ui";

import { ImgLoader } from "@treegram/loaders-img";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPaste } from "@fortawesome/pro-solid-svg-icons";
import { faRulerHorizontal } from "@fortawesome/pro-regular-svg-icons";

import useViewerContext from "@/services/context/viewer/instance/use-viewer-context.ts";
import useProjectFormNavigationContext from "@/pages/takeoff/context/project-form-navigation/use-project-form-navigation-context.ts";

import Card from "@/components/card";
import Button from "@/components/buttons/button";
import ToggleButton from "@/components/buttons/button-toggle";
import ButtonLockDependent from "@/components/buttons/button-lock-dependent";
import useStoreyMeasurementContext from "@/pages/takeoff/layouts/measures/layouts/storey-measures/context/measurement/use-storey-measurement-context.ts";
import useProjectMeasureContext from "@/pages/takeoff/context/project-measure/use-project-measure-context.ts";
import useStoreyMeasuresContext
    from "@/pages/takeoff/layouts/measures/layouts/storey-measures/context/measures/use-storey-measures-context.ts";
import {
    Measure3dViewerRef
} from "@/pages/takeoff/layouts/measures/layouts/storey-measures/components/measure-3d-viewer-container.tsx";
import useModelingToolsContext from "@/services/context/viewer/modeling-tools/use-modeling-tools-context.ts";

interface StoreyMeasuresToolbarProps {
    viewerRef: React.RefObject<Measure3dViewerRef | null>;
    closeMeasuresCreation: () => void;
}

const StoreyMeasuresToolbar: React.FC<StoreyMeasuresToolbarProps> = ({ viewerRef, closeMeasuresCreation }) => {

    const { storeysFileData } = useProjectMeasureContext();
    const { storey } = useStoreyMeasuresContext();

    const { setRoute } = useProjectFormNavigationContext();
    const { viewer, getLoader, getTool } = useViewerContext();

    const { isMeasurementToolOpen, toggleMeasurementTool, } = useStoreyMeasurementContext();
    const { isInDrawingMode, isInEditingMode } = useModelingToolsContext();

    const [viewMode, setViewMode] = useState<OBC.CameraProjection>("Perspective");
    const [sliderValue, setSliderValue] = useState(0);

    useEffect(() => {
        if (viewer == null) return;
        const loader = getLoader(ImgLoader);
        if (loader == null) return;

        const altimetryToApply = sliderValue * 0.01;
        loader.updateOptions({ altimetry: altimetryToApply });
    }, [viewer, getLoader, sliderValue]);

    const handlePerspectiveChange = useCallback(() => {
        const mode = viewMode === "Perspective" ? "Orthographic" : "Perspective";
        setViewMode(mode);
        getTool(CameraTool)?.setProjection(mode);
    }, [viewMode, getTool]);

    const files = storeysFileData.get(storey.id) ?? [];

    return (
        <Card additionalContentClasses='flex flex-row gap-x-0.75 items-center pl-1.5 pr-0.5' additionalClasses='h-fit px-1 pointer-events-auto py-2'>
            <Slider.Root
                className='relative flex items-center w-24 touch-none select-none mr-2'
                orientation='horizontal' min={-30} max={30} step={1} defaultValue={[0]}
                value={[sliderValue]}
                onValueChange={(value) => setSliderValue(value[0])}>
                <Slider.Track className='relative grow h-1 rounded bg-gray-100'>
                    <Slider.Range className='absolute h-full rounded-full bg-blue-500' />
                </Slider.Track>
                <Slider.Thumb className='block size-3 rounded-full bg-blue-300 shadow-lg hover:bg-blue-400' />
            </Slider.Root>

            <div className='border-s border-gray-300 h-4' />

            <Button onClick={handlePerspectiveChange} variant='ghost' size='2xs'>
                <img src={viewMode === "Perspective" ? "/images/icons/cube-96.png" : "/images/icons/orthogonal-view-96.png"}
                     alt={viewMode === "Perspective" ? "Cube Icon" : "Orthogonal Icon"} className='size-4.5' />
            </Button>

            <ToggleButton variant='ghost' size='xs' className='cursor-pointer'
                toggled={isMeasurementToolOpen}
                setIsToggled={(state) => {
                    if (state && (isInDrawingMode || isInEditingMode)) {
                        closeMeasuresCreation();
                    }

                    toggleMeasurementTool(state);

                    if (state) {
                        viewerRef.current?.startMeasurement();
                    } else {
                        viewerRef.current?.stopMeasurement();
                    }
                }}
                disabled={isInDrawingMode || isInEditingMode}
                data-tooltip-id='measure-data-card-tooltip' data-tooltip-content="Mesurer une distance">
                <FontAwesomeIcon icon={faRulerHorizontal} className='text-sm' />
            </ToggleButton>
            
            <div className='border-s border-gray-300 h-4' />
            
            <ButtonLockDependent variant='ghost' size='2xs' className='cursor-pointer'
                                 data-tooltip-id='measure-data-card-tooltip' data-tooltip-content='Récupérer tous les métrés d’un autre plan'
                                 onClick={() => {
                                     setRoute('copy', new URLSearchParams({ fileId: files[0].fileId }))
                                 }}>
                <FontAwesomeIcon icon={faPaste} />
            </ButtonLockDependent>
        </Card>
    );
};

export default StoreyMeasuresToolbar;
